(ns rothko.form2.select.single-select.component
  (:require [reagent.core :as r]
            [rothko.component.core :refer [defcomp]]
            [rothko.component.on-click-out :as on-click-out]
            [rothko.dom.event :as dom-event]
            [rothko.dom.input :as dom-input]
            [rothko.form2.component :as form]
            [rothko.form2.label.component :as label]
            [rothko.form2.select.core :as select]
            [rothko.form2.validation-message.component :as validation-message]
            [rothko.tooltip.component :as tooltip]))

(defn value->representation
  [value options]
  (some (fn [{option-value :value
              option-representation :representation}]
          (when (= value option-value)
            option-representation))
        options))

(defn select-option
  ;; rothko#273
  [{:keys [options] :as state} {:keys [value representation] :as option}]
  ;; There are two ways in which this function is used to update state.

  ;; One is during normal form usage and is triggered by mouse clicks
  ;; and the enter key. In this case we have access to the full
  ;; options with both the UI representation and the underlying value.

  ;; In the second case we're rehydrating the form state from the form
  ;; value alone (the `id` arg). In this case we need to search
  ;; through the field options for the visual representation that
  ;; corresponds to the current id. This is only done once during the
  ;; form creation.
  (let [representation (or representation (value->representation value options))]
    (assoc state
           :input representation
           :selected option
           :highlighted option
           :show-options? false)))

(defn highlight-option-at
  [{:keys [highlighted visible-options] :as state} at]
  (let [index (.indexOf visible-options highlighted)
        new-index (case at
                    :previous (if (pos? index)
                                (dec index)
                                (dec (count visible-options)))
                    :next (if (< index (dec (count visible-options)))
                            (inc index)
                            0))]
    (assoc state
           :index new-index
           :highlighted (nth visible-options new-index))))

(def capture-key? #{"Enter" "ArrowDown" "ArrowUp"})

(defn input-key-down-handler
  [!state {:keys [::form/set-value!]
           :as   _field-props} event]
  (when-let [{:keys [key]
              :as   _event} (dom-input/event->key event)]
    (when (capture-key? key)
      (dom-event/stop event))
    (case key
      "ArrowDown" (swap! !state highlight-option-at :next)
      "Enter" (do
                (dom-input/deselect event)
                (let [highlighted (:highlighted @!state)]
                  (set-value! (:value highlighted))
                  (swap! !state select-option highlighted)))
      "ArrowUp" (swap! !state highlight-option-at :previous)
      :no-op)))

(defn input-change-handler
  [!state {:keys [::form/set-value-cleanly!] :as _field-props} input-value]
  (set-value-cleanly! nil)
  (swap! !state
         (fn [{:keys [options selected] :as state}]
           (let [visible-options (->> options (select/visible-options input-value) vec)
                 index (.indexOf visible-options selected)
                 selected-no-longer-visible? (neg? index)]
             (cond-> state
               selected-no-longer-visible? (assoc :highlighted (first visible-options)))))))

(defn input-on-focus-handler
  [!state _ _]
  (swap! !state assoc
         :visible-options (:options @!state)))

(defn select-input
  [!state field-props]
  (let [on-blur (partial select/blur-handler field-props)
        on-change (dom-event/dom-event
                   (comp (partial input-change-handler !state field-props)
                         (partial select/input-change-handler !state field-props)))
        on-focus (comp (partial input-on-focus-handler !state field-props)
                       (partial select/input-on-focus-handler !state field-props))
        on-key-down (partial input-key-down-handler !state field-props)]
    (fn input-inner
      [_ {:keys [appearance ::form/disabled? ::form/placeholder] :as _field-props}]
      (let [{:keys [input selected]} @!state]
        [:input.select__input
         {:autoComplete :off
          :class (conj appearance :select)
          :data-value (:id selected)
          :disabled disabled?
          :on-blur on-blur
          :on-change on-change
          :on-focus on-focus
          :on-key-down on-key-down
          :placeholder placeholder
          :type :text
          :value input}]))))

(defn option-component
  [!state {:keys [::form/set-value!] :as _field-props} {:keys [representation tooltip] :as option}]
  (let [highlighted? (= option (:highlighted @!state))]
    [:div.select__option-container
     [:div.select__option
      {:class (when highlighted? [:select__option--selected])
       ;; `on-mouse-down` is used as `on-click` too late in the event handling cycle
       :on-mouse-down (fn []
                        (set-value! (:value option))
                        (swap! !state select-option option))}
      (if tooltip (tooltip/inline-tooltip representation tooltip)
                  [:span representation])]]))

;; rothko#210 use defcomp for input and assert props there
(defcomp field
  [{:keys [::form/set-value-cleanly!
           ::form/value
           ::select/options]
    :as field-props}]
  (let [!state (r/atom
                {:input ""
                 :options options
                 :selected nil
                 :highlighted (first options)
                 :show-options? false
                 :visible-options options})
        option-component (partial option-component !state field-props)
        on-click-out (partial select/close-options-handler !state)]
    (set-value-cleanly! value)
    (when value (swap! !state select-option {:value value}))
    (on-click-out/with-on-click-out
      {:on-click-out on-click-out}
      (fn select-inner
        [{:keys [appearance ref]
          :as field-props}]
        (let [{:keys [show-options? visible-options]} @!state]
          [:div.form__select
           [label/component field-props]
           [:div.select {:class appearance
                         :ref ref}
            [select-input !state field-props]
            (when (and show-options?
                       (seq visible-options))
              (into [:div.select__options]
                    (map option-component visible-options)))
            [validation-message/component field-props]]])))))
